"use strict";
import { onReady } from '../core/ready.js';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

Swiper.use([Navigation]);

/**
 * ScrollTrigger PIN su model.php (ordine DOM: hero → modelDescription → model-video-block → modelVariants → modelFeatures → inspirations → …)
 * Per evitare sovrapposizioni: .model-features ha z-index: 60 (sopra .model-video-block .panel-inner 50).
 *
 * Pin attivi:
 * 1. modelDescriptionPinTrigger  – #modelDescription, solo mobile, initModelDescriptionPin()
 * 2. modelFeaturesMobileScrollTrigger – .model-features (#modelFeatures), solo mobile, initModelFeaturesMobileSync()
 * 3. inspirationPinTrigger         – #inspirations, initInspirationPin()
 * 4. printablePinTrigger           – #printableAreas, solo desktop, initModelPage() usePinScroll
 */

// ID unici di model/variant page — NON usare .model-front/side-image:
// quelle classi sono anche sulle catalog-card e querySelector prenderebbe la prima card.
const modelFrontImageSelector = '#modelFrontImage';
const modelSideImageSelector = '#modelSideImage';
const modelPageRootSelector = '.model-page, .variant-page';
const printablePanelId = 'printableAreas';
const techFeaturesPanelId = 'techFeatures';
const inspirationPanelId = 'inspirations';
const printableItemSelector = '.printable-area-item';
const printableRectId = 'printableAreaRect';
const modelNavId = 'modelPageNav';
const modelHeroSelector = '.model-hero';
const headerSelector = '#pageHeader';
const variantCardSelector = '.group-variant-card';
const variantCardImageSelector = '.variant-card-image .variant-image';
const variantSwatchesSelector = '.variant-card-swatches';
const variantSwatchSelector = '.variant-swatch';
/**
 * Lunghezza fisica di riferimento della penna (mm) mappata sull'altezza del box immagine visibile.
 * Serve a convertire width/height area stampabile in px proporzionati alla penna (come nei PDF catalogo).
 */
const REFERENCE_PEN_LENGTH_MM = 140;
/**
 * Scala dimensione #printableAreaRect rispetto alla proporzione mm→penna.
 * 1 = fedele al riferimento; <1 più piccolo; >1 più grande.
 */
const PRINTABLE_RECT_SIZE_SCALE = 0.9;
/** Safe area inferiore per barrel: punta vicino al bordo viewport */
const MOBILE_BARREL_BOTTOM_SAFE = 'calc(1rem + env(safe-area-inset-bottom, 0px))';
let modelNavUpdateHandler = null;
let modelNavTween = null;
let modelNavIsVisible = false;
let modelFeaturesMobileSyncHandler = null;
let modelFeaturesImageMode = null;
let lastTechTouchedNavbar = false;
let lastInspirationsTouchedNavbar = false;
const isTouchDevice = typeof window !== 'undefined'
    && (('ontouchstart' in window)
        || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
        || navigator.maxTouchPoints > 0
        || navigator.msMaxTouchPoints > 0);

const printableState = {
    panel: null,
    items: [],
    index: 0,
    active: false,
    lockedScroll: false
};

/** Ultimi argomenti di updatePrintableRect (per ricalcolo su resize / fine tween). */
let lastPrintableRectArgs = null;
let printableRectRefreshRaf = 0;

const MODEL_COLS_MOBILE_BREAKPOINT = 991;
const SECTION_CHANGE_EVENT = 'section:change';

/** Stato sezione attualmente in vista (scroll nativo + Lenis) */
let currentSectionIndex = 0;
let currentSection = null;
let previousSectionIndex = null;
let previousSection = null;
let sectionObserverCleanup = null;

/** scrollTop del pannello che mette la voce `index` in cima alla vista (getBoundingClientRect per funzionare con flex/qualunque layout) */
function getScrollTopForItem(panel, items, index) {
    if (!panel || !items?.length || index < 0) return 0;
    const item = items[index];
    if (!item) return 0;
    const panelRect = panel.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const delta = itemRect.top - panelRect.top;
    return Math.max(0, panel.scrollTop + delta);
}

/** indice della voce “attiva” in base allo scrollTop del pannello (stessa logica getBoundingClientRect) */
function getPrintableIndexFromScrollTop(panel, items, scrollTop) {
    if (!panel || !items?.length) return 0;
    const panelRect = panel.getBoundingClientRect();
    for (let i = 0; i < items.length; i += 1) {
        const itemRect = items[i].getBoundingClientRect();
        const itemStart = panel.scrollTop + (itemRect.top - panelRect.top);
        const itemEnd = itemStart + items[i].offsetHeight;
        if (scrollTop >= itemStart && scrollTop < itemEnd) return i;
        if (scrollTop < itemStart) return Math.max(0, i - 1);
    }
    return Math.max(0, items.length - 1);
}

/** Aggiorna stato e UI dalla posizione di scroll (ScrollTrigger = fonte di verità) */
function syncPrintableActiveFromScroll(panel, items) {
    if (!panel || !items?.length) return;
    const scrollTop = panel.scrollTop;
    const index = getPrintableIndexFromScrollTop(panel, items, scrollTop);
    const item = items[index];
    if (!item) return;
    printableState.index = index;
    const component = item?.dataset?.component || null;
    setActivePrintableItem(panel, item);
    applyPrintableAreaView(component, item);
}

let printableScrollTrigger = null;
let printablePinTrigger = null;
let modelDescriptionLogTrigger = null;
let modelDescriptionPinTrigger = null;
let inspirationPinTrigger = null;
let modelFeaturesMobileDriverHandler = null;
let modelFeaturesMobileTimelineActive = false;
let modelFeaturesMobileScrollTrigger = null;
const PRINTABLE_PINNED_CLASS = 'printable-pinned';
const INSPIRATION_PINNED_CLASS = 'inspiration-pinned';
const modelDescriptionId = 'modelDescription';
const MODEL_DESC_PINNED_CLASS = 'model-description-pinned';
let lastMobilePrintableIndex = null;
let techPanelHideDone = false;
let techPanelWasHidden = false;
let printablePanelShowDone = false;
let printablePanelFadeStarted = false;

function isPrintablePanelActive() {
    return currentSection?.id === printablePanelId;
}

const clearActivePrintableItems = (panel) => {
    if (!panel) return;
    panel.querySelectorAll(printableItemSelector).forEach((item) => item.classList.remove('active'));
};

const setActivePrintableItem = (panel, item) => {
    if (!panel || !item) return;
    panel.querySelectorAll(printableItemSelector).forEach((el) => {
        el.classList.toggle('active', el === item);
    });
};

const hidePrintableRect = () => {
    const rect = document.getElementById(printableRectId);
    lastPrintableRectArgs = null;

    if (rect) {
        rect.style.opacity = 0;
        rect.style.visibility = 'hidden';
        rect.style.transform = '';
        rect.style.left = '';
        rect.style.top = '';
        rect.style.width = '';
        rect.style.height = '';
    }
};

/** Offset esplicito (anche 0). Stringa vuota / null → fallback. */
const parseOptionalOffset = (value) => {
    if (value === '' || value == null) return null;
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : null;
};

const isPenImageShown = (el) => {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && Number.parseFloat(style.opacity || '0') > 0.05;
};

/** Penna attualmente visibile (side se attiva, altrimenti front). */
const getVisiblePenImage = () => {
    const front = document.querySelector(modelFrontImageSelector);
    const side = document.querySelector(modelSideImageSelector);
    if (isPenImageShown(side)) return side;
    if (isPenImageShown(front)) return front;
    return front || side || null;
};

/**
 * Metriche penna + media in coordinate locali del containing block (.model-features-media).
 * Usa getBoundingClientRect (include scale/translate GSAP) e le riporta in px locali.
 */
const getPenAnchorMetrics = () => {
    const media = document.querySelector('.model-features-media');
    const pen = getVisiblePenImage();
    if (!media || !pen) return null;

    const mediaBox = media.getBoundingClientRect();
    const penBox = pen.getBoundingClientRect();
    if (mediaBox.width < 1 || mediaBox.height < 1 || penBox.width < 1 || penBox.height < 1) {
        return null;
    }

    const mediaLocalW = media.offsetWidth || mediaBox.width;
    const mediaLocalH = media.offsetHeight || mediaBox.height;
    const mediaScaleX = mediaBox.width / mediaLocalW;
    const mediaScaleY = mediaBox.height / mediaLocalH;

    return {
        media,
        pen,
        // Centro penna in px locali del media
        centerX: (penBox.left + penBox.width / 2 - mediaBox.left) / mediaScaleX,
        centerY: (penBox.top + penBox.height / 2 - mediaBox.top) / mediaScaleY,
        // Dimensioni penna in px locali (per convertire offset %)
        width: penBox.width / mediaScaleX,
        height: penBox.height / mediaScaleY,
        mediaScaleX,
        mediaScaleY
    };
};

/** Ricalcola il rect attivo (resize / fine animazione penna). */
const refreshActivePrintableRect = () => {
    if (!lastPrintableRectArgs) return;
    updatePrintableRect(...lastPrintableRectArgs);
};

const schedulePrintableRectRefresh = () => {
    if (!lastPrintableRectArgs) return;
    if (printableRectRefreshRaf) return;
    printableRectRefreshRaf = requestAnimationFrame(() => {
        printableRectRefreshRaf = 0;
        refreshActivePrintableRect();
    });
};

/**
 * Posiziona #printableAreaRect rispetto al box reale della penna visibile.
 * offsetX/Y restano in % della larghezza/altezza penna (0,0 = centro penna).
 */
const updatePrintableRect = (
    widthMm,
    heightMm,
    index,
    offsetX = 0,
    offsetY = 0,
    component = '',
    offsetXMobile = '',
    offsetYMobile = ''
) => {
    const rect = document.getElementById(printableRectId);
    if (!rect) return;

    lastPrintableRectArgs = [
        widthMm,
        heightMm,
        index,
        offsetX,
        offsetY,
        component,
        offsetXMobile,
        offsetYMobile
    ];

    const width = parseFloat(widthMm);
    const height = parseFloat(heightMm);
    const desktopX = Number.parseFloat(offsetX);
    const desktopY = Number.parseFloat(offsetY);
    const safeDesktopX = Number.isFinite(desktopX) ? desktopX : 0;
    const safeDesktopY = Number.isFinite(desktopY) ? desktopY : 0;

    if (!Number.isFinite(width) || !Number.isFinite(height)) {
        rect.style.opacity = 0;
        rect.style.visibility = 'hidden';
        return;
    }

    const anchor = getPenAnchorMetrics();
    if (!anchor) {
        rect.style.opacity = 0;
        rect.style.visibility = 'hidden';
        return;
    }

    const isMobile = isModelColsMobileViewport();
    const mobileX = parseOptionalOffset(offsetXMobile);
    const mobileY = parseOptionalOffset(offsetYMobile);
    const offsetPctX = isMobile && mobileX != null ? mobileX : safeDesktopX;
    const offsetPctY = isMobile && mobileY != null ? mobileY : safeDesktopY;

    // Proporzioni da PDF: i mm dell'area sono in scala con l'altezza penna disegnata
    // (non più mm×costante fissa, che restava piccola mentre la penna andava in scale 2).
    const pxPerMm = (anchor.height / REFERENCE_PEN_LENGTH_MM) * PRINTABLE_RECT_SIZE_SCALE;
    const pxWidth = width * pxPerMm;
    const pxHeight = height * pxPerMm;

    const drawOffsetX = (offsetPctX / 100) * anchor.width;
    const drawOffsetY = (offsetPctY / 100) * anchor.height;

    rect.textContent = index != null ? index : '';
    rect.style.opacity = 1;
    rect.style.visibility = 'visible';
    rect.style.width = `${pxWidth}px`;
    rect.style.height = `${pxHeight}px`;
    rect.style.left = `${anchor.centerX + drawOffsetX}px`;
    rect.style.top = `${anchor.centerY + drawOffsetY}px`;
    // Centra il box sul punto ancora (centro penna + offset %)
    rect.style.transform = 'translate(-50%, -50%)';
};

const resetPrintableAreaView = () => {
    const modelFrontImage = document.querySelector(modelFrontImageSelector);
    const modelSideImage = document.querySelector(modelSideImageSelector);
    const printablePanel = document.getElementById(printablePanelId);
    const media = document.querySelector('.model-features-media');
    const pen = document.querySelector('.model-features-pen');

    if (modelFrontImage) {
        gsap.to(modelFrontImage, {
            autoAlpha: 1,
            scale: 1,
            position: '',
            top: '',
            duration: 0.3,
            clearProps: 'transform,top,left,position'
        });
    }

    if (modelSideImage) {
        gsap.to(modelSideImage, {
            autoAlpha: 0,
            scale: 1,
            bottom: '',
            duration: 0.3,
            clearProps: 'bottom'
        });
    }

    if (isModelColsMobileViewport() && media) {
        gsap.set(media, { scale: 1, clearProps: 'scale' });
    }
    if (pen) gsap.set(pen, { clearProps: 'transform,scale' });

    clearActivePrintableItems(printablePanel);
    hidePrintableRect();
    modelFeaturesImageMode = 'front';
    printableState.panel = null;
    printableState.items = [];
    printableState.index = 0;
    printableState.active = false;
    if (printableState.lockedScroll) {
        document.body.classList.remove('no-scroll');
        printableState.lockedScroll = false;
    }
};

/** Su mobile: centratura orizzontale (left 50% + translateX -50%) */
const centerModelImagesMobile = () => {
    if (!isModelColsMobileViewport()) return;
    const modelFrontImage = document.querySelector(modelFrontImageSelector);
    const modelSideImage = document.querySelector(modelSideImageSelector);
    if (modelFrontImage) {
        gsap.set(modelFrontImage, { clearProps: 'transform' });
        gsap.set(modelFrontImage, { left: '50%', xPercent: -50, x: 0 });
    }
    if (modelSideImage) {
        gsap.set(modelSideImage, { clearProps: 'transform' });
        gsap.set(modelSideImage, { left: '50%', xPercent: -50, x: 0 });
    }
};

/** Risolve front|side da data-pen-image (CMS) con fallback sul componente. */
const resolvePenImageView = (itemEl, component) => {
    const raw = itemEl?.getAttribute?.('data-pen-image') || itemEl?.dataset?.penImage || '';
    let value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (value === 'side' || value.includes('side') || value.includes('lateral')) {
        return 'side';
    }
    if (value === 'front' || value.includes('front') || value.includes('fronte')) {
        return 'front';
    }
    // Legacy: barrel → side, resto → front
    return component === 'barrel' ? 'side' : 'front';
};

/**
 * Mostra esattamente la foto richiesta da pen_image.
 * Visibilità/z-index subito con gsap.set (niente race con setModelFeaturesImageMode).
 */
const applyPrintableAreaView = (component, itemEl) => {
    const modelFrontImage = document.querySelector(modelFrontImageSelector);
    const modelSideImage = document.querySelector(modelSideImageSelector);

    if (!modelFrontImage || !modelSideImage) return;

    const isMobile = isModelColsMobileViewport();
    const useSideImage = resolvePenImageView(itemEl, component) === 'side';
    // Posizionamento: sempre legato al componente (barrel = basso, resto = sotto lista / top)
    const isBarrelLayout = component === 'barrel';

    const showImage = useSideImage ? modelSideImage : modelFrontImage;
    const hideImage = useSideImage ? modelFrontImage : modelSideImage;

    const updateRectFromItem = () => {
        if (!itemEl) return;
        updatePrintableRect(
            itemEl.dataset.width,
            itemEl.dataset.height,
            itemEl.dataset.index,
            itemEl.dataset.offsetX,
            itemEl.dataset.offsetY,
            itemEl.dataset.component,
            itemEl.dataset.offsetXMobile,
            itemEl.dataset.offsetYMobile
        );
    };

    gsap.killTweensOf([modelFrontImage, modelSideImage]);

    // .model-side-image ha z-index:-1 in CSS: senza alzarla resta dietro e sembra la front
    gsap.set(hideImage, { autoAlpha: 0, zIndex: -1 });
    gsap.set(showImage, { autoAlpha: 1, zIndex: 2 });

    if (isBarrelLayout) {
        if (isMobile) {
            centerModelImagesMobile();
            gsap.to(showImage, {
                scale: 1,
                bottom: MOBILE_BARREL_BOTTOM_SAFE,
                top: 'auto',
                position: 'absolute',
                left: '50%',
                xPercent: -50,
                x: 0,
                zIndex: 2,
                duration: 0.3,
                overwrite: true,
                onUpdate: updateRectFromItem,
                onComplete: () => {
                    centerModelImagesMobile();
                    updateRectFromItem();
                }
            });
        } else {
            gsap.to(showImage, {
                scale: useSideImage ? 1.75 : 2,
                translateX: '0',
                bottom: '15vh',
                top: 'auto',
                position: 'absolute',
                zIndex: 2,
                duration: 0.3,
                overwrite: true,
                onUpdate: updateRectFromItem,
                onComplete: updateRectFromItem
            });
        }

        updateRectFromItem();
        return;
    }

    if (isMobile) {
        centerModelImagesMobile();
        const getTopBelowPrintableList = () => {
            const printablePanel = document.getElementById(printablePanelId);
            const listEl = printablePanel?.querySelector('.printable-areas-list');
            const mediaEl = document.querySelector('.model-features-media');
            if (listEl && mediaEl) {
                const listRect = listEl.getBoundingClientRect();
                const mediaRect = mediaEl.getBoundingClientRect();
                return `${Math.max(0, listRect.bottom - mediaRect.top)}px`;
            }
            return '0';
        };
        const topValue = getTopBelowPrintableList();
        gsap.to(showImage, {
            scale: 1,
            position: 'absolute',
            top: topValue,
            bottom: 'auto',
            left: '50%',
            xPercent: -50,
            x: 0,
            zIndex: 2,
            transformOrigin: '50% 0%',
            duration: 0.3,
            overwrite: true,
            onUpdate: updateRectFromItem,
            onComplete: () => {
                gsap.set(showImage, { top: getTopBelowPrintableList(), zIndex: 2, autoAlpha: 1 });
                centerModelImagesMobile();
                updateRectFromItem();
            }
        });
    } else {
        gsap.to(showImage, {
            scale: useSideImage ? 1.75 : 2,
            position: 'absolute',
            top: '30vh',
            bottom: 'auto',
            zIndex: 2,
            transformOrigin: '50% 50%',
            duration: 0.3,
            overwrite: true,
            onUpdate: updateRectFromItem,
            onComplete: updateRectFromItem
        });
    }

    updateRectFromItem();
    if (isMobile) centerModelImagesMobile();
};

const setModelFeaturesImageMode = (mode = 'front') => {
    const targetMode = mode === 'printable' ? 'printable' : 'front';
    if (modelFeaturesImageMode === targetMode) return;
    modelFeaturesImageMode = targetMode;

    const modelFrontImage = document.querySelector(modelFrontImageSelector);
    const modelSideImage = document.querySelector(modelSideImageSelector);
    if (!modelFrontImage || !modelSideImage) return;

    const isMobile = isModelColsMobileViewport();

    // In printable la foto la decide solo applyPrintableAreaView (pen_image).
    // Qui non forzare la side: altrimenti race GSAP e side/front risultano invertiti.
    if (targetMode === 'printable') {
        if (isMobile) centerModelImagesMobile();
        return;
    }

    gsap.killTweensOf([modelFrontImage, modelSideImage]);

    if (isMobile) {
        centerModelImagesMobile();
    }
    gsap.set(modelFrontImage, { autoAlpha: 1, zIndex: 2 });
    gsap.set(modelSideImage, { autoAlpha: 0, zIndex: -1 });
    gsap.to(modelFrontImage, {
        scale: 1,
        position: isMobile ? 'absolute' : '',
        top: isMobile ? '0' : '',
        left: isMobile ? '50%' : '',
        xPercent: isMobile ? -50 : undefined,
        zIndex: 2,
        duration: 0.3,
        overwrite: true,
        clearProps: isMobile ? '' : 'transform,top,left,position,bottom',
        onComplete: isMobile ? centerModelImagesMobile : undefined
    });
    gsap.to(modelSideImage, {
        scale: 1,
        bottom: '',
        zIndex: -1,
        duration: 0.3,
        overwrite: true,
        left: isMobile ? '50%' : '',
        xPercent: isMobile ? -50 : undefined,
        x: isMobile ? 0 : undefined,
        clearProps: isMobile ? '' : 'transform,bottom',
        onComplete: isMobile ? centerModelImagesMobile : undefined
    });
    if (isMobile) centerModelImagesMobile();
};

const inspirationState = {
    swiper: null,
    active: false,
    lockedScroll: false
};

const isModelColsMobileViewport = () => window.matchMedia(`(max-width: ${MODEL_COLS_MOBILE_BREAKPOINT}px)`).matches;

const getMobileNavbarOffset = () => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--navbar-mobile-height');
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : 0;
};

const getNavbarOffset = () => {
    const prop = isModelColsMobileViewport() ? '--navbar-mobile-height' : '--navbar-desktop-height';
    const raw = getComputedStyle(document.documentElement).getPropertyValue(prop);
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : 68;
};

const checkInspirationsNavbarTouch = () => {
    const inspirationsEl = document.getElementById(inspirationPanelId);
    if (!(inspirationsEl instanceof HTMLElement)) return;
    if (inspirationsEl.classList.contains(INSPIRATION_PINNED_CLASS)) return;
    const navbarOffset = getNavbarOffset();
    const top = inspirationsEl.getBoundingClientRect().top;
    const threshold = 5;
    if (top <= navbarOffset + threshold) {
        if (!lastInspirationsTouchedNavbar) {
            lastInspirationsTouchedNavbar = true;
        }
    } else if (top > navbarOffset + 20) {
        lastInspirationsTouchedNavbar = false;
    }
};

const teardownInspirationPin = () => {
    if (inspirationPinTrigger) {
        inspirationPinTrigger.kill();
        inspirationPinTrigger = null;
    }
    const el = document.getElementById(inspirationPanelId);
    if (el) el.classList.remove(INSPIRATION_PINNED_CLASS);
    lastInspirationsTouchedNavbar = false;
    inspirationState.active = false;
    if (inspirationState.lockedScroll) {
        document.body.classList.remove('no-scroll');
        inspirationState.lockedScroll = false;
    }
};

/** Restituisce true se ci sono slide non visibili (contenuto che supera il container) */
const hasInspirationSlidesOverflow = (swiper) => {
    if (!swiper?.slides?.length) return false;
    const containerWidth = swiper.el?.offsetWidth ?? 0;
    const totalSlidesWidth = [...swiper.slides].reduce((sum, s) => sum + (s.offsetWidth || 0), 0);
    return totalSlidesWidth > containerWidth;
};

const INSPIRATION_SLIDE_DURATION_MS = 400;
/** Moltiplicatore per la distanza di scroll del pin: < 1 = più sensibile (slide avanzano con meno scroll) */
const INSPIRATION_SCROLL_SENSITIVITY = 0.55;

const initInspirationPin = () => {
    const section = document.getElementById(inspirationPanelId);
    const swiper = inspirationState.swiper;
    if (!(section instanceof HTMLElement) || !swiper?.slides?.length) return;
    if (!hasInspirationSlidesOverflow(swiper)) return;

    teardownInspirationPin();

    const navbarOffset = getNavbarOffset();
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    const slideCount = swiper.slides.length;
    const pinScrollDistance = slideCount * vh * INSPIRATION_SCROLL_SENSITIVITY;
    let lastDrivenIndex = -1;

    inspirationPinTrigger = ScrollTrigger.create({
        trigger: section,
        start: `top ${navbarOffset}px`,
        end: `+=${pinScrollDistance}`,
        pin: section,
        pinSpacing: true,
        refreshPriority: 0,
        onEnter: (self) => {
            if (self.direction !== 1) return;
            section.classList.add(INSPIRATION_PINNED_CLASS);
            inspirationState.active = true;
            lastDrivenIndex = -1;
            swiper.slideTo(0, INSPIRATION_SLIDE_DURATION_MS);
        },
        onLeaveBack: () => {
            section.classList.remove(INSPIRATION_PINNED_CLASS);
            lastInspirationsTouchedNavbar = false;
            inspirationState.active = false;
            lastDrivenIndex = -1;
        },
        onLeave: () => {
            section.classList.remove(INSPIRATION_PINNED_CLASS);
            lastInspirationsTouchedNavbar = false;
            inspirationState.active = false;
            lastDrivenIndex = -1;
        },
        onEnterBack: (self) => {
            if (self.direction !== 1) return;
            section.classList.add(INSPIRATION_PINNED_CLASS);
            inspirationState.active = true;
            lastDrivenIndex = -1;
        },
        onUpdate: (self) => {
            const progress = Math.min(1, Math.max(0, self.progress));
            const n = slideCount;
            const index = n <= 1 ? 0 : Math.min(Math.floor(progress * n), n - 1);
            if (index !== lastDrivenIndex) {
                lastDrivenIndex = index;
                swiper.slideTo(index, INSPIRATION_SLIDE_DURATION_MS);
            }
        }
    });
};

const clamp01 = (value) => Math.max(0, Math.min(1, value));
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (Math.pow(-2 * t + 2, 2) / 2));

const cleanupModelFeaturesMobileSync = () => {
    if (modelFeaturesMobileScrollTrigger) {
        modelFeaturesMobileScrollTrigger.kill();
        modelFeaturesMobileScrollTrigger = null;
    }
    if (modelFeaturesMobileSyncHandler) {
        window.removeEventListener('resize', modelFeaturesMobileSyncHandler);
        modelFeaturesMobileSyncHandler = null;
    }
};

/** Moltiplicatore > 1 = più distanza di scroll per le stesse fasi (rallenta se scroll veloce) */
const MODEL_FEATURES_SCROLL_SLOWDOWN = 1.55;

const getModelFeaturesMobilePhases = () => {
    const navbarH = getMobileNavbarOffset();
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    const base = Math.max(260, vh - navbarH);
    const compact = window.matchMedia('(max-width: 767px)').matches;
    const k = MODEL_FEATURES_SCROLL_SLOWDOWN;
    const phaseA = base * (compact ? 0.62 : 0.68) * k;
    const phaseB = base * (compact ? 0.62 : 0.68) * k;
    const phaseC = base * (compact ? 1.15 : 1.08) * k;
    const phaseD = base * (compact ? 0.66 : 0.58) * k;
    return { phaseA, phaseB, phaseC, phaseD, total: phaseA + phaseB + phaseC + phaseD };
};

const resetModelFeaturesMobileTimelineStyles = () => {
    const section = document.querySelector('.model-page .model-features, .variant-page .model-features');
    if (!(section instanceof HTMLElement)) return;
    const media = section.querySelector('.model-features-media');
    const imageWrap = section.querySelector('.model-features-image');
    const techPanel = section.querySelector(`#${techFeaturesPanelId}`);
    const printablePanel = section.querySelector(`#${printablePanelId}`);
    const modelFrontImage = section.querySelector(modelFrontImageSelector);
    const modelSideImage = section.querySelector(modelSideImageSelector);
    if (!(techPanel instanceof HTMLElement) || !(printablePanel instanceof HTMLElement)) return;

    modelFeaturesMobileTimelineActive = false;
    if (media) gsap.set(media, { clearProps: 'transform,opacity,x,y,scale' });
    const pen = section.querySelector('.model-features-pen');
    if (pen) gsap.set(pen, { clearProps: 'transform,scale' });
    if (imageWrap) gsap.set(imageWrap, { clearProps: 'transform,opacity,x,y' });
    if (modelFrontImage) gsap.set(modelFrontImage, { clearProps: 'top,left,position,bottom,transform' });
    if (modelSideImage) gsap.set(modelSideImage, { clearProps: 'top,left,position,bottom,transform' });
    techPanel.style.display = '';
    techPanel.style.visibility = '';
    printablePanel.style.display = '';
    printablePanel.style.visibility = '';
    printablePanel.querySelectorAll(printableItemSelector).forEach((el) => {
        el.style.display = '';
        el.style.visibility = '';
    });
    const rect = section.querySelector(`#${printableRectId}`);
    if (rect) {
        rect.style.opacity = '';
        rect.style.visibility = '';
    }
    lastMobilePrintableIndex = null;
    techPanelHideDone = false;
    techPanelWasHidden = false;
    printablePanelShowDone = false;
    printablePanelFadeStarted = false;
    resetPrintableAreaView();
};

const teardownModelFeaturesMobileTimeline = () => {
    cleanupModelFeaturesMobileSync();
    modelFeaturesMobileTimelineActive = false;
    resetModelFeaturesMobileTimelineStyles();
};

/** Mobile (< 992px): 4 fasi su progress di scroll senza pin/fixed (A su, B giu, C printable, D uscita) */
const initModelFeaturesMobileSync = () => {
    cleanupModelFeaturesMobileSync();
    const section = document.querySelector('.model-page .model-features, .variant-page .model-features');
    if (!(section instanceof HTMLElement)) return;
    const techPanel = section.querySelector(`#${techFeaturesPanelId}`);
    const printablePanel = section.querySelector(`#${printablePanelId}`);
    const media = section.querySelector('.model-features-media');
    const imageWrap = section.querySelector('.model-features-image');
    const rect = section.querySelector(`#${printableRectId}`);
    if (!(techPanel instanceof HTMLElement) || !(printablePanel instanceof HTMLElement) || !(media instanceof HTMLElement) || !(imageWrap instanceof HTMLElement)) return;

    const items = Array.from(printablePanel.querySelectorAll(printableItemSelector));

    const runTimeline = (dist, phases, navbarH) => {
        const { phaseA, phaseB, phaseC, phaseD, total } = phases;
        const clamped = Math.max(0, Math.min(total, dist));
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        const mediaHeight = media.offsetHeight || (vh * 0.5);
        const inRange = dist >= 0 && dist <= total;
        modelFeaturesMobileTimelineActive = inRange;

        // Step 1: entrata (tech visibile, printable nascosta e non visibile)
        if (dist <= 0) {
            techPanelHideDone = false;
            printablePanelShowDone = false;
            printablePanelFadeStarted = false;
            setModelFeaturesImageMode('front');
            const modelFrontImg = document.querySelector(modelFrontImageSelector);
            if (modelFrontImg) gsap.set(modelFrontImg, { top: '0', clearProps: 'top' });
            printablePanel.style.display = 'none';
            printablePanel.style.visibility = 'hidden';
            gsap.set(printablePanel, { autoAlpha: 0, y: 40, x: 0 });
            if (techPanelWasHidden) {
                techPanel.style.display = '';
                techPanel.style.visibility = 'visible';
                gsap.fromTo(techPanel, { y: -40, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.55, ease: 'power1.out' });
                techPanelWasHidden = false;
            } else {
                techPanel.style.display = '';
                techPanel.style.visibility = 'visible';
                gsap.set(techPanel, { autoAlpha: 1, y: 0, x: 0 });
            }
            // posizione iniziale penna: nascoste prima del translate (fade-in in phase A)
            gsap.set(media, { y: vh * 0.5, x: 0, opacity: 0 });
            gsap.set(imageWrap, { y: 0, x: 0, opacity: 0 });
            items.forEach((el) => {
                el.style.display = '';
                el.style.visibility = '';
            });
            hidePrintableRect();
            return;
        }

        // Step 6: uscita (fade + slide)
        if (dist >= total) {
            const lastIndex = Math.max(items.length - 1, 0);
            const lastItem = items[lastIndex];
            setModelFeaturesImageMode('printable');
            techPanel.style.display = 'none';
            techPanel.style.visibility = 'hidden';
            printablePanel.style.display = '';
            printablePanel.style.visibility = 'visible';
            gsap.set(printablePanel, { autoAlpha: 1, y: 0 });
            items.forEach((el, i) => {
                el.style.display = i === lastIndex ? '' : 'none';
                el.style.visibility = i === lastIndex ? 'visible' : 'hidden';
            });
            if (lastItem) {
                setActivePrintableItem(printablePanel, lastItem);
                applyPrintableAreaView(lastItem?.dataset?.component ?? null, lastItem);
            }
            gsap.set(media, { y: mediaHeight * 0.22, opacity: 0 });
            gsap.set(imageWrap, { y: mediaHeight * 0.08, opacity: 0 });
            if (rect) {
                rect.style.opacity = '0';
                rect.style.visibility = 'hidden';
            }
            return;
        }

        const phaseABound = phaseA;
        const phaseBBound = phaseA + phaseB;
        const phaseCBound = phaseA + phaseB + phaseC;
        const phaseDBound = total;

        if (clamped <= phaseABound) {
            // Step 2: penna scorre dal basso verso l'alto (effetto parallasse: immagine più lenta dello scroll)
            const t = easeInOut(clamp01(clamped / phaseA));
            const tParallax = Math.pow(t, 0.78); // parallax: image lags, più naturale
            techPanelHideDone = false;
            setModelFeaturesImageMode('front');
            const _frontImg = document.querySelector(modelFrontImageSelector);
            if (_frontImg) gsap.set(_frontImg, { top: '0', clearProps: 'top' });
            if (techPanelWasHidden) {
                techPanel.style.display = '';
                techPanel.style.visibility = 'visible';
                gsap.fromTo(techPanel, { y: -40, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.55, ease: 'power1.out' });
                techPanelWasHidden = false;
            } else {
                techPanel.style.display = '';
                techPanel.style.visibility = 'visible';
                gsap.set(techPanel, { autoAlpha: 1, y: 0, x: 0 });
            }
            printablePanel.style.display = 'none';
            printablePanel.style.visibility = 'hidden';
            gsap.set(printablePanel, { autoAlpha: 0, y: 40, x: 0 });
            items.forEach((el) => {
                el.style.display = '';
                el.style.visibility = '';
            });
            const startY = vh * 0.5;
            const endY = -vh * 0.25;
            const currentY = startY + (endY - startY) * tParallax;
            // fade-in penne legato al parallasse: da 0 a 1 durante phase A
            const penOpacity = Math.pow(tParallax, 0.7);
            gsap.set(media, { y: currentY, x: 0, opacity: penOpacity });
            gsap.set(imageWrap, { y: 0, x: 0, opacity: penOpacity });
            hidePrintableRect();
            return;
        }

        if (clamped <= phaseBBound) {
            // Step 3: penna sale ancora e esce sopra (parallasse). Appena esce, techFeatures sparisce.
            const local = clamped - phaseABound;
            const t = easeInOut(clamp01(local / phaseB));
            const tParallax = Math.pow(t, 0.78);
            setModelFeaturesImageMode('front');
            const modelFrontImg = document.querySelector(modelFrontImageSelector);
            if (modelFrontImg) gsap.set(modelFrontImg, { top: '0', clearProps: 'top' });

            printablePanel.style.display = 'none';
            printablePanel.style.visibility = 'hidden';
            gsap.set(printablePanel, { autoAlpha: 0, y: 40, x: 0 });
            items.forEach((el) => {
                el.style.display = '';
                el.style.visibility = '';
            });

            const startY = -vh * 0.25;
            const endY = -vh * 0.9;
            const currentY = startY + (endY - startY) * tParallax;
            gsap.set(media, { y: currentY, x: 0, opacity: 1 });
            gsap.set(imageWrap, { y: 0, x: 0, opacity: 1 });

            const penIsOut = currentY <= -vh * 0.75;
            if (penIsOut) {
                if (!techPanelHideDone) {
                    techPanel.style.display = '';
                    techPanel.style.visibility = 'visible';
                    gsap.to(techPanel, {
                        y: -40,
                        autoAlpha: 0,
                        duration: 0.55,
                        ease: 'power1.inOut',
                        onComplete: () => {
                            techPanel.style.display = 'none';
                            techPanel.style.visibility = 'hidden';
                            techPanelHideDone = true;
                            techPanelWasHidden = true;
                        }
                    });
                }
            } else {
                techPanelHideDone = false;
                if (techPanelWasHidden) {
                    techPanel.style.display = '';
                    techPanel.style.visibility = 'visible';
                    gsap.fromTo(techPanel, { y: -40, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.55, ease: 'power1.out' });
                    techPanelWasHidden = false;
                } else {
                    techPanel.style.display = '';
                    techPanel.style.visibility = 'visible';
                    gsap.set(techPanel, { autoAlpha: 1, y: 0, x: 0 });
                }
            }

            hidePrintableRect();
            return;
        }

        if (clamped <= phaseCBound) {
            // Step 4/5: solo printableAreas; scroll più lento tra gli item (curva per non saltare 1→3)
            const local = clamped - phaseBBound;
            const t = easeInOut(clamp01(local / phaseC));
            const n = items.length;
            const progress = Math.pow(t, 0.55) * n; // più lento: serve più scroll per passare all'item successivo
            const index = n <= 1 ? 0 : Math.min(Math.floor(progress), n - 1);
            const activeItem = items[index];

            setModelFeaturesImageMode('printable');

            // Tech: fadeOut prima di nascondere; non forzare display:none qui (uccide l'animazione)
            if (!techPanelHideDone) {
                techPanel.style.display = '';
                techPanel.style.visibility = 'visible';
                gsap.to(techPanel, {
                    y: -40,
                    autoAlpha: 0,
                    duration: 0.55,
                    ease: 'power1.inOut',
                    onComplete: () => {
                        techPanel.style.display = 'none';
                        techPanel.style.visibility = 'hidden';
                        techPanelHideDone = true;
                        techPanelWasHidden = true;
                    }
                });
            } else {
                techPanel.style.display = 'none';
                techPanel.style.visibility = 'hidden';
                gsap.set(techPanel, { autoAlpha: 0, y: -40, x: 0 });
            }

            // printableAreas: fadeIn (una sola volta), poi mostrato; non gsap.set prima della fine (uccide il fadeIn)
            printablePanel.style.display = '';
            if (!printablePanelShowDone) {
                if (!printablePanelFadeStarted) {
                    printablePanelFadeStarted = true;
                    gsap.set(printablePanel, { y: 32, autoAlpha: 0, visibility: 'visible', x: 0 });
                    gsap.to(printablePanel, {
                        y: 0,
                        autoAlpha: 1,
                        duration: 0.55,
                        ease: 'power1.out',
                        onComplete: () => { printablePanelShowDone = true; }
                    });
                }
            } else {
                printablePanel.style.visibility = 'visible';
                gsap.set(printablePanel, { autoAlpha: 1, y: 0, x: 0 });
            }

            items.forEach((el, i) => {
                const visible = i === index;
                el.style.display = visible ? '' : 'none';
                el.style.visibility = visible ? 'visible' : 'hidden';
            });
            if (activeItem) {
                printableState.panel = printablePanel;
                printableState.items = items;
                printableState.index = index;
                printableState.active = true;

                if (lastMobilePrintableIndex !== index) {
                    const previousItem = lastMobilePrintableIndex != null ? items[lastMobilePrintableIndex] : null;
                    if (previousItem) {
                        gsap.set(previousItem, { autoAlpha: 0, y: 0 });
                    }
                    gsap.set(activeItem, { autoAlpha: 1, y: 0 });
                    lastMobilePrintableIndex = index;
                }

                setActivePrintableItem(printablePanel, activeItem);
                applyPrintableAreaView(activeItem?.dataset?.component ?? null, activeItem);
            }

            // penna centrata (niente zoom: stessa scala del rect)
            gsap.set(media, { y: 0, x: 0, opacity: 1 });
            gsap.set(imageWrap, { y: 0, x: 0, opacity: 1 });
            schedulePrintableRectRefresh();
            return;
        }

        // Step 6: uscita printable, penna in fade (sempre centrata)
        const local = clamped - phaseCBound;
        const t = easeInOut(clamp01(local / (phaseDBound - phaseCBound || phaseD)));
        const n = items.length;
        const index = n <= 1 ? 0 : n - 1;
        const activeItem = items[index];
        setModelFeaturesImageMode('printable');
        techPanel.style.display = 'none';
        techPanel.style.visibility = 'hidden';
        gsap.set(techPanel, { autoAlpha: 0, y: 0, x: 0 });
        printablePanel.style.display = '';
        printablePanel.style.visibility = 'visible';
        gsap.set(printablePanel, { autoAlpha: 1, y: 0, x: 0 });
        items.forEach((el, i) => {
            const visible = i === index;
            el.style.display = visible ? '' : 'none';
            el.style.visibility = visible ? 'visible' : 'hidden';
        });
        if (activeItem) {
            if (lastMobilePrintableIndex !== index) {
                lastMobilePrintableIndex = index;
            }
            setActivePrintableItem(printablePanel, activeItem);
            applyPrintableAreaView(activeItem?.dataset?.component ?? null, activeItem);
        }
        gsap.set(media, { y: 0, x: 0, opacity: 1 - t });
        gsap.set(imageWrap, { y: 0, x: 0, opacity: 1 - t });
        schedulePrintableRectRefresh();
        if (rect) {
            rect.style.opacity = `${1 - t}`;
            rect.style.visibility = t >= 0.99 ? 'hidden' : 'visible';
        }
    };

    const setupPinnedTimeline = () => {
        if (!isModelColsMobileViewport()) {
            teardownModelFeaturesMobileTimeline();
            return;
        }

        const navbarH = getMobileNavbarOffset();
        const phases = getModelFeaturesMobilePhases();

        // Assicurati che il wrapper immagine usi il layout CSS di default
        imageWrap.style.position = '';
        imageWrap.style.top = '';
        imageWrap.style.left = '';
        imageWrap.style.right = '';
        imageWrap.style.bottom = '';
        imageWrap.style.transform = '';
        imageWrap.style.width = '';
        imageWrap.style.height = '';
        imageWrap.style.maxHeight = '';

        if (modelFeaturesMobileScrollTrigger) {
            modelFeaturesMobileScrollTrigger.kill();
            modelFeaturesMobileScrollTrigger = null;
        }

        // Stato iniziale identico a runTimeline(0): tech visibile, printable nascosta, penne opacity 0 (fade-in in phase A)
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        setModelFeaturesImageMode('front');
        techPanel.style.display = '';
        techPanel.style.visibility = 'visible';
        gsap.set(techPanel, { autoAlpha: 1, y: 0, x: 0 });
        printablePanel.style.display = 'none';
        printablePanel.style.visibility = 'hidden';
        gsap.set(printablePanel, { autoAlpha: 0, y: 40, x: 0 });
        gsap.set(media, { y: vh * 0.5, x: 0, opacity: 0 });
        gsap.set(imageWrap, { y: 0, x: 0, opacity: 0 });
        items.forEach((el) => {
            el.style.display = '';
            el.style.visibility = '';
        });
        centerModelImagesMobile();

        modelFeaturesMobileScrollTrigger = ScrollTrigger.create({
            trigger: section,
            start: `top ${navbarH}px`,
            end: `+=${phases.total}`,
            pin: section,
            pinSpacing: true,
            refreshPriority: 1,
            onEnter: () => {
                runTimeline(0, phases, navbarH);
            },
            onLeaveBack: () => {
                resetModelFeaturesMobileTimelineStyles();
            },
            onUpdate: (self) => {
                const dist = clamp01(self.progress) * phases.total;
                runTimeline(dist, phases, navbarH);
            },
            onLeave: () => {
                runTimeline(phases.total, phases, navbarH);
            }
        });

        modelFeaturesMobileTimelineActive = true;
    };

    modelFeaturesMobileSyncHandler = () => {
        setupPinnedTimeline();
    };

    window.addEventListener('resize', modelFeaturesMobileSyncHandler);
    setupPinnedTimeline();
};

const exitPrintableArea = () => {
    printableState.active = false;
    if (printableState.lockedScroll) {
        document.body.classList.remove('no-scroll');
        printableState.lockedScroll = false;
    }
};

const resetInspirationState = () => {
    inspirationState.active = false;
    if (inspirationState.lockedScroll) {
        document.body.classList.remove('no-scroll');
        inspirationState.lockedScroll = false;
    }
};

const exitInspirationBlock = () => {
    resetInspirationState();
};

const handleSectionChange = (event) => {
    if (modelFeaturesMobileTimelineActive && isModelColsMobileViewport()) return;
    const detail = event?.detail || {};
    const currentPanel = detail.currentPanel || null;
    const previousIndex = Number.isFinite(detail.previousIndex) ? detail.previousIndex : null;
    const currentIndex = Number.isFinite(detail.currentIndex) ? detail.currentIndex : null;
    const isEnteringFromBottom = previousIndex != null && currentIndex != null && previousIndex > currentIndex;

    if (!currentPanel || currentPanel.id !== printablePanelId) {
        resetPrintableAreaView();
    }

    if (!currentPanel || currentPanel.id !== inspirationPanelId) {
        resetInspirationState();
    }

    if (currentPanel?.id === techFeaturesPanelId) {
        setModelFeaturesImageMode('front');
    }

    if (!currentPanel) return;

    if (currentPanel.id === inspirationPanelId && inspirationState.swiper) {
        const targetIndex = isEnteringFromBottom
            ? Math.max(inspirationState.swiper.slides.length - 1, 0)
            : 0;
        inspirationState.swiper.slideTo(targetIndex, 0);
        inspirationState.active = true;
        return;
    }

    if (currentPanel.id !== printablePanelId) return;

    const items = Array.from(currentPanel.querySelectorAll(printableItemSelector));
    printableState.panel = currentPanel;
    printableState.items = items;
    printableState.active = items.length > 0;

    setModelFeaturesImageMode('printable');

    const scrollable = currentPanel.closest('[data-scrollable]') || currentPanel;
    if (scrollable && scrollable.scrollHeight > scrollable.clientHeight) {
        const targetScrollTop = isEnteringFromBottom
            ? scrollable.scrollHeight - scrollable.clientHeight
            : 0;
        scrollable.scrollTop = targetScrollTop;
        syncPrintableActiveFromScroll(currentPanel, items);
    } else {
        printableState.index = isEnteringFromBottom ? Math.max(items.length - 1, 0) : 0;
        const item = items[printableState.index];
        if (item) {
            setActivePrintableItem(currentPanel, item);
            applyPrintableAreaView(item?.dataset?.component || null, item);
        }
    }
};

const scrollPageToY = (y, { duration = 0.75 } = {}) => {
    const target = Math.max(0, Number(y) || 0);
    const lenis = window.__lenis;
    if (lenis && typeof lenis.scrollTo === 'function') {
        lenis.scrollTo(target, { duration, force: true });
        return;
    }
    window.scrollTo({ top: target, behavior: 'smooth' });
};

/**
 * Scrolla la pagina alla progress del pin desktop corrispondente all'indice area.
 * (Su desktop le aree sono guidate dallo scroll di pagina, non da panel.scrollTop.)
 */
const scrollDesktopPrintableToIndex = (index, items) => {
    if (!printablePinTrigger || !items?.length) return false;
    const n = items.length;
    const start = printablePinTrigger.start;
    const end = printablePinTrigger.end;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return false;
    const clamped = Math.max(0, Math.min(index, n - 1));
    // Metà del segmento dell'item, così onUpdate seleziona quell'indice in modo stabile
    const progress = n <= 1 ? 0 : Math.min((clamped + 0.5) / n, 0.999);
    scrollPageToY(start + progress * (end - start));
    return true;
};

const handlePrintableItemClick = (event) => {
    // Solo desktop: su mobile la timeline ScrollTrigger gestisce già le aree
    if (isModelColsMobileViewport()) return;

    const printablePanel = document.getElementById(printablePanelId);
    if (!printablePanel) return;
    const targetItem = event.currentTarget;
    const items = Array.from(printablePanel.querySelectorAll(printableItemSelector));
    const index = items.indexOf(targetItem);
    if (index < 0) return;

    printableState.panel = printablePanel;
    printableState.items = items;
    printableState.active = true;
    printableState.index = index;

    setActivePrintableItem(printablePanel, targetItem);
    applyPrintableAreaView(targetItem?.dataset?.component || null, targetItem);

    if (scrollDesktopPrintableToIndex(index, items)) return;

    // Fallback se il pin non è ancora attivo
    const targetScrollTop = getScrollTopForItem(printablePanel, items, index);
    gsap.to(printablePanel, { scrollTop: targetScrollTop, duration: 0.3 });
};

const goToPrintableIndex = (nextIndex) => {
    if (!printableState.active) return false;
    const items = printableState.items;
    const panel = printableState.panel;
    if (!panel || !items?.length) return false;
    const clamped = Math.max(0, Math.min(nextIndex, items.length - 1));

    if (!isModelColsMobileViewport() && scrollDesktopPrintableToIndex(clamped, items)) {
        const item = items[clamped];
        if (item) {
            printableState.index = clamped;
            setActivePrintableItem(panel, item);
            applyPrintableAreaView(item?.dataset?.component || null, item);
        }
        return true;
    }

    const targetScrollTop = getScrollTopForItem(panel, items, clamped);
    gsap.to(panel, { scrollTop: targetScrollTop, duration: 0.35, ease: 'power2.out' });
    return true;
};

const shouldShowModelNav = (heroEl, headerHeight = 0) => {
    if (!heroEl) return false;
    const heroBottom = heroEl.getBoundingClientRect().bottom;
    return heroBottom <= (headerHeight + 1);
};

const cleanupModelNav = () => {
    if (!modelNavUpdateHandler) return;
    window.removeEventListener('scroll', modelNavUpdateHandler);
    window.removeEventListener('resize', modelNavUpdateHandler);
    window.removeEventListener(SECTION_CHANGE_EVENT, modelNavUpdateHandler);
    modelNavUpdateHandler = null;
    if (modelNavTween) {
        modelNavTween.kill();
        modelNavTween = null;
    }
    modelNavIsVisible = false;
};

const initModelNav = () => {
    cleanupModelNav();

    const navEl = document.getElementById(modelNavId);
    const heroEl = document.querySelector(modelHeroSelector);
    const headerEl = document.querySelector(headerSelector);

    if (!navEl || !heroEl) return;

    const updateNav = () => {
        if (isModelColsMobileViewport()) {
            if (modelNavTween) {
                modelNavTween.kill();
                modelNavTween = null;
            }
            navEl.style.display = 'none';
            navEl.style.pointerEvents = '';
            gsap.set(navEl, { clearProps: 'opacity,visibility,transform' });
            modelNavIsVisible = false;
            return;
        }

        const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
        const shouldBeVisible = shouldShowModelNav(heroEl, headerHeight);
        navEl.style.top = `${headerHeight}px`;

        if (shouldBeVisible === modelNavIsVisible) return;

        if (modelNavTween) {
            modelNavTween.kill();
            modelNavTween = null;
        }

        if (shouldBeVisible) {
            navEl.style.display = 'block';
            navEl.style.pointerEvents = 'none';
            modelNavTween = gsap.fromTo(navEl,
                { autoAlpha: 0, y: -10 },
                {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.28,
                    ease: 'power2.out',
                    onComplete: () => {
                        navEl.style.pointerEvents = '';
                        modelNavTween = null;
                    }
                }
            );
            modelNavIsVisible = true;
            return;
        }

        navEl.style.pointerEvents = 'none';
        modelNavTween = gsap.to(navEl, {
            autoAlpha: 0,
            y: -10,
            duration: 0.2,
            ease: 'power2.in',
            onComplete: () => {
                navEl.style.display = 'none';
                navEl.style.pointerEvents = '';
                gsap.set(navEl, { clearProps: 'opacity,visibility,transform' });
                modelNavTween = null;
            }
        });
        modelNavIsVisible = false;
    };

    modelNavUpdateHandler = updateNav;

    updateNav();
    window.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', updateNav);
    window.addEventListener(SECTION_CHANGE_EVENT, updateNav);
};

const initVariantCardSwatchHover = () => {
    const cards = document.querySelectorAll(variantCardSelector);
    if (!cards.length) return;

    cards.forEach((card) => {
        const cardImage = card.querySelector(variantCardImageSelector);
        const swatchesContainer = card.querySelector(variantSwatchesSelector);
        if (!(cardImage instanceof HTMLImageElement) || !(swatchesContainer instanceof HTMLElement)) return;
        if (swatchesContainer.dataset.hoverReady === '1') return;

        swatchesContainer.dataset.hoverReady = '1';

        const defaultSrc = cardImage.getAttribute('src') || '';
        const defaultSrcset = cardImage.getAttribute('srcset');
        const defaultSizes = cardImage.getAttribute('sizes');

        const restoreDefaultImage = () => {
            if (defaultSrc) {
                cardImage.setAttribute('src', defaultSrc);
            }

            if (defaultSrcset) {
                cardImage.setAttribute('srcset', defaultSrcset);
            } else {
                cardImage.removeAttribute('srcset');
            }

            if (defaultSizes) {
                cardImage.setAttribute('sizes', defaultSizes);
            } else {
                cardImage.removeAttribute('sizes');
            }
        };

        const setVariantImage = (swatch) => {
            const variantImage = swatch?.dataset?.variantImage || '';
            if (!variantImage) return;
            cardImage.setAttribute('src', variantImage);
            cardImage.removeAttribute('srcset');
            cardImage.removeAttribute('sizes');
        };

        const swatches = swatchesContainer.querySelectorAll(variantSwatchSelector);
        swatches.forEach((swatch) => {
            swatch.addEventListener('mouseenter', () => setVariantImage(swatch));
            swatch.addEventListener('focus', () => setVariantImage(swatch));
        });

        swatchesContainer.addEventListener('mouseleave', restoreDefaultImage);
        swatchesContainer.addEventListener('focusout', (event) => {
            const nextFocusedElement = event.relatedTarget;
            if (!(nextFocusedElement instanceof Node) || !swatchesContainer.contains(nextFocusedElement)) {
                restoreDefaultImage();
            }
        });
    });
};

/** Scroll to top on load so mobile model-features calculations start from a known state */
function scrollToTopOnModelPageLoad() {
    const hasModelFeatures = document.querySelector('.model-page .model-features, .variant-page .model-features');
    if (!hasModelFeatures) return;
    requestAnimationFrame(() => {
        const lenis = window.__lenis;
        if (lenis && typeof lenis.scrollTo === 'function') {
            lenis.scrollTo(0, { immediate: true, force: true });
        } else {
            window.scrollTo(0, 0);
        }
    });
}

onReady(() => {
    scrollToTopOnModelPageLoad();
    initModelPage();
});

function initSectionObserver() {
    sectionObserverCleanup?.();
    const panels = [...document.querySelectorAll('.full-panel')];
    if (!panels.length) return;

    const header = document.querySelector('.page-header');
    const getHeaderH = () => header ? header.getBoundingClientRect().height : 0;

    function getMostVisibleIndex() {
        const h = getHeaderH();
        let bestIndex = 0;
        let bestRatio = 0;
        panels.forEach((el, i) => {
            const rect = el.getBoundingClientRect();
            const visibleTop = Math.max(rect.top, h);
            const visibleBottom = Math.min(rect.bottom, window.innerHeight);
            const visibleH = Math.max(0, visibleBottom - visibleTop);
            const ratio = rect.height > 0 ? visibleH / rect.height : 0;
            if (ratio > bestRatio) {
                bestRatio = ratio;
                bestIndex = i;
            }
        });
        return bestIndex;
    }

    function updateSection() {
        const i = getMostVisibleIndex();
        if (i === currentSectionIndex && panels[i] === currentSection) return;
        previousSectionIndex = currentSectionIndex;
        previousSection = currentSection;
        currentSectionIndex = i;
        currentSection = panels[i] || null;
        const detail = {
            root: document.body,
            currentIndex: currentSectionIndex,
            previousIndex: previousSectionIndex,
            currentPanel: currentSection,
            previousPanel: previousSection,
            currentPanelHtml: currentSection?.innerHTML || ''
        };
        window.dispatchEvent(new CustomEvent(SECTION_CHANGE_EVENT, { detail }));
    }

    let tick = null;
    const onScroll = () => {
        if (!tick) {
            tick = requestAnimationFrame(() => {
                updateSection();
                checkInspirationsNavbarTouch();
                tick = null;
            });
        }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    updateSection();

    sectionObserverCleanup = () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        sectionObserverCleanup = null;
    };
}

function initModelPage() {
    // main.js importa model.js su tutte le pagine: senza guard, resetPrintableAreaView()
    // applicava gsap autoAlpha sulla prima .model-side-image del catalogo.
    if (!document.querySelector(modelPageRootSelector)) return;

    window.removeEventListener(SECTION_CHANGE_EVENT, handleSectionChange);
    teardownModelFeaturesMobileTimeline();

    const modelVariantsSlider = document.getElementById('modelVariantsSlider');
    const printablePanel = document.getElementById(printablePanelId);

    initSectionObserver();
    window.addEventListener(SECTION_CHANGE_EVENT, handleSectionChange);

    // Ensure printable area animations don't leak stale inline styles into model-features.
    resetPrintableAreaView();
    resetInspirationState();

    initModelNav();

    /* Pin in ordine top-to-bottom (modelDescription → modelFeatures → …) così i pin-spacer non saltano model-video-block e modelVariants. V. refreshPriority in GSAP. */
    const teardownModelDescriptionPin = () => {
        if (modelDescriptionPinTrigger) {
            modelDescriptionPinTrigger.kill();
            modelDescriptionPinTrigger = null;
            requestAnimationFrame(() => ScrollTrigger.refresh());
        }
        const section = document.getElementById(modelDescriptionId);
        if (section) section.classList.remove(MODEL_DESC_PINNED_CLASS);
    };
    const initModelDescriptionPin = () => {
        const section = document.getElementById(modelDescriptionId);
        if (!section || !isModelColsMobileViewport()) return;
        const scrollable = section.querySelector('.panel-column-scrollable');
        const panels = section.querySelectorAll('.model-cols-panel');
        const navbarH = getMobileNavbarOffset();
        const vh = window.innerHeight || document.documentElement.clientHeight;

        teardownModelDescriptionPin();
        modelDescriptionLogTrigger?.kill();
        modelDescriptionLogTrigger = null;

        if (panels.length > 0 && scrollable) {
            const pinScrollDistance = panels.length * vh;

            modelDescriptionPinTrigger = ScrollTrigger.create({
                trigger: section,
                start: `top ${navbarH}px`,
                end: `+=${pinScrollDistance}`,
                pin: section,
                pinSpacing: true,
                refreshPriority: 2,
                onEnter: (self) => {
                    if (self.direction !== 1) return;
                    section.classList.add(MODEL_DESC_PINNED_CLASS);
                    scrollable.scrollTop = 0;
                },
                onLeaveBack: () => {
                    section.classList.remove(MODEL_DESC_PINNED_CLASS);
                },
                onLeave: () => {
                    section.classList.remove(MODEL_DESC_PINNED_CLASS);
                },
                onEnterBack: (self) => {
                    if (self.direction !== 1) return;
                    section.classList.add(MODEL_DESC_PINNED_CLASS);
                },
                onUpdate: (self) => {
                    const progress = self.progress;
                    const n = panels.length;
                    const index = n <= 1 ? 0 : Math.min(Math.floor(progress * n), n - 1);
                    scrollable.scrollTop = getScrollTopForItem(scrollable, Array.from(panels), index);
                }
            });
        }
    };
    const onModelDescriptionResize = () => {
        if (!isModelColsMobileViewport()) {
            teardownModelDescriptionPin();
            modelDescriptionLogTrigger?.kill();
            modelDescriptionLogTrigger = null;
            requestAnimationFrame(() => ScrollTrigger.refresh());
        } else {
            initModelDescriptionPin();
            requestAnimationFrame(() => ScrollTrigger.refresh());
        }
    };
    initModelDescriptionPin();
    window.addEventListener('resize', onModelDescriptionResize);

    const applyModelFeaturesViewportMode = () => {
        if (isModelColsMobileViewport() && document.querySelector('.model-page .model-features, .variant-page .model-features') && document.getElementById(printablePanelId)) {
            initModelFeaturesMobileSync();
        } else {
            teardownModelFeaturesMobileTimeline();
        }
        requestAnimationFrame(() => ScrollTrigger.refresh());
    };
    applyModelFeaturesViewportMode();
    window.addEventListener('resize', applyModelFeaturesViewportMode);

    /* Un refresh dopo che i pin top-to-bottom sono stati creati, così le sezioni tra un pin-spacer e l’altro (model-video-block, modelVariants) restano nel flusso. */
    requestAnimationFrame(() => {
        requestAnimationFrame(() => ScrollTrigger.refresh());
    });

    if (printablePanel) {
        const printableItems = printablePanel.querySelectorAll(printableItemSelector);
        printableItems.forEach((item) => {
            item.removeEventListener('click', handlePrintableItemClick);
            item.addEventListener('click', handlePrintableItemClick);
        });

        const items = Array.from(printableItems);
        const usePinScroll = !isModelColsMobileViewport() && items.length > 0;

        if (printablePinTrigger) {
            printablePinTrigger.kill();
            printablePinTrigger = null;
        }

        if (usePinScroll) {
            const vh = window.innerHeight || document.documentElement.clientHeight;
            const pinScrollDistance = items.length * vh;
            const startScroll = () => Math.max(1, printablePanel.getBoundingClientRect().top + window.scrollY);

            printablePinTrigger = ScrollTrigger.create({
                trigger: printablePanel,
                start: startScroll,
                end: () => startScroll() + pinScrollDistance,
                pin: printablePanel,
                pinSpacing: true,
                onEnter: (self) => {
                    if (self.direction !== 1) return;
                    printablePanel.classList.add(PRINTABLE_PINNED_CLASS);
                    printablePanel.style.top = '0px';
                    setModelFeaturesImageMode('printable');
                    printableState.panel = printablePanel;
                    printableState.items = items;
                    printableState.active = true;
                    printableState.index = 0;
                    const first = items[0];
                    if (first) {
                        setActivePrintableItem(printablePanel, first);
                        applyPrintableAreaView(first?.dataset?.component || null, first);
                        printablePanel.scrollTop = 0;
                    }
                },
                onLeaveBack: () => {
                    printablePanel.classList.remove(PRINTABLE_PINNED_CLASS);
                    printablePanel.style.top = '';
                    resetPrintableAreaView();
                },
                onLeave: () => {
                    printablePanel.classList.remove(PRINTABLE_PINNED_CLASS);
                    printablePanel.style.top = '';
                    printableState.active = false;
                },
                onEnterBack: (self) => {
                    if (self.direction !== 1) return;
                    printablePanel.classList.add(PRINTABLE_PINNED_CLASS);
                    printablePanel.style.top = '0px';
                    printableState.active = true;
                },
                onUpdate: (self) => {
                    printablePanel.style.top = '0px';
                    const progress = self.progress;
                    const n = items.length;
                    const index = n <= 1 ? 0 : Math.min(Math.floor(progress * n), n - 1);
                    const item = items[index];
                    if (!item) return;
                    printableState.index = index;
                    setActivePrintableItem(printablePanel, item);
                    applyPrintableAreaView(item?.dataset?.component || null, item);
                    printablePanel.scrollTop = getScrollTopForItem(printablePanel, items, index);
                }
            });
            printableScrollTrigger?.kill();
            printableScrollTrigger = null;
        } else {
            printableScrollTrigger?.kill();
            const list = printablePanel.querySelector('.printable-areas-list');
            printableScrollTrigger = ScrollTrigger.create({
                trigger: list || printablePanel,
                scroller: printablePanel,
                start: 'top top',
                end: 'bottom bottom',
                onUpdate: () => {
                    if (isPrintablePanelActive() && printableState.panel && printableState.items?.length) {
                        syncPrintableActiveFromScroll(printableState.panel, printableState.items);
                    }
                }
            });
        }
    }

    const onPrintableResize = () => {
        schedulePrintableRectRefresh();
        const panel = document.getElementById(printablePanelId);
        if (!panel) return;
        const items = panel.querySelectorAll(printableItemSelector);
        const usePin = !isModelColsMobileViewport() && items.length > 0;
        if (usePin && !printablePinTrigger) {
            printablePinTrigger?.kill();
            printableScrollTrigger?.kill();
            printableScrollTrigger = null;
            const itemsArr = Array.from(items);
            const vh = window.innerHeight || document.documentElement.clientHeight;
            const pinScrollDistance = itemsArr.length * vh;
            const startScroll = () => Math.max(1, panel.getBoundingClientRect().top + window.scrollY);
            printablePinTrigger = ScrollTrigger.create({
                trigger: panel,
                start: startScroll,
                end: () => startScroll() + pinScrollDistance,
                pin: panel,
                pinSpacing: true,
                onEnter: (self) => {
                    if (self.direction !== 1) return;
                    panel.classList.add(PRINTABLE_PINNED_CLASS);
                    panel.style.top = '0px';
                    setModelFeaturesImageMode('printable');
                    printableState.panel = panel;
                    printableState.items = itemsArr;
                    printableState.active = true;
                    printableState.index = 0;
                    const first = itemsArr[0];
                    if (first) {
                        setActivePrintableItem(panel, first);
                        applyPrintableAreaView(first?.dataset?.component || null, first);
                        panel.scrollTop = 0;
                    }
                },
                onLeaveBack: () => { panel.classList.remove(PRINTABLE_PINNED_CLASS); panel.style.top = ''; resetPrintableAreaView(); },
                onLeave: () => { panel.classList.remove(PRINTABLE_PINNED_CLASS); panel.style.top = ''; printableState.active = false; },
                onEnterBack: (self) => {
                    if (self.direction !== 1) return;
                    panel.classList.add(PRINTABLE_PINNED_CLASS);
                    panel.style.top = '0px';
                    printableState.active = true;
                },
                onUpdate: (self) => {
                    panel.style.top = '0px';
                    const n = itemsArr.length;
                    const index = n <= 1 ? 0 : Math.min(Math.floor(self.progress * n), n - 1);
                    const item = itemsArr[index];
                    if (!item) return;
                    printableState.index = index;
                    setActivePrintableItem(panel, item);
                    applyPrintableAreaView(item?.dataset?.component || null, item);
                    panel.scrollTop = getScrollTopForItem(panel, itemsArr, index);
                }
            });
        } else if (!usePin) {
            if (printablePinTrigger) {
                printablePinTrigger.kill();
                printablePinTrigger = null;
                panel?.classList.remove(PRINTABLE_PINNED_CLASS);
                requestAnimationFrame(() => ScrollTrigger.refresh());
            }
            if (!printableScrollTrigger) {
                const list = panel.querySelector('.printable-areas-list');
                printableScrollTrigger = ScrollTrigger.create({
                    trigger: list || panel,
                    scroller: panel,
                    start: 'top top',
                    end: 'bottom bottom',
                    onUpdate: () => {
                        if (isPrintablePanelActive() && printableState.panel && printableState.items?.length) {
                            syncPrintableActiveFromScroll(printableState.panel, printableState.items);
                        }
                    }
                });
            }
        } else if (printablePinTrigger) {
            requestAnimationFrame(() => ScrollTrigger.refresh());
        }
    };
    window.addEventListener('resize', onPrintableResize);

    if (modelVariantsSlider) {
        const swiperModelVariants = new Swiper('.model-variants-slider', {
            loop: false,
            slidesPerView: 'auto',
            centeredSlides: false,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            breakpoints: {
                0: {
                    spaceBetween: 12,
                    slidesOffsetBefore: 12,
                },
                768: {
                    spaceBetween: 32,
                    slidesOffsetBefore: 32,
                }
            }
        });
    }
    initVariantCardSwatchHover();

    const inspirationsSliderEl = document.getElementById('inspirationsSlider');
    if (inspirationsSliderEl) {
        const inspirationsSlider = new Swiper(inspirationsSliderEl, {
            loop: false,
            centeredSlides: false,
            freeMode: false,
            speed: 450,
            allowTouchMove: true,
            slidesPerView: 'auto',
            navigation: {
                enabled: false
            }
        });
        inspirationState.swiper = inspirationsSlider;
        initInspirationPin();
        requestAnimationFrame(() => ScrollTrigger.refresh());
    }

    const relatedModelsSwiperEl = document.getElementById('relatedModelsSwiper');
    if (relatedModelsSwiperEl) {
        const relatedModelsSwiper = new Swiper(relatedModelsSwiperEl, {
            loop: false,
            
            autoplay: true,
            navigation: {
                enabled: false
            },
            breakpoints: {
                0: {
                    slidesPerView: 1,
                },
                768: {
                    slidesPerView: 2,
                }
            }
        });
    }


}
